import React, { FC, useCallback } from 'react';
import { useIntl } from 'react-intl';
import {
  TIMEZONE_OPTIONS,
  type TimezoneOption,
} from '../../../utils/util/timezone';
import { myOptionFilter } from '../../../utils/util/util';
import SelectInput from './SelectInput';

interface Props {
  value?: TimezoneOption | null;
  onChange: (option: TimezoneOption) => void;
  onValidChange?: (validationMessage: string | null) => void;
  name?: string;
  required?: boolean;
  disabled?: boolean;
  isSubmitting?: boolean;
  clearable?: boolean;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
}

/**
 * Picker for an IANA timezone, for use as a ValidatedForm input:
 *
 *   { type: INPUT_TYPES.CUSTOM_COMPONENT, component: TimezoneSelector,
 *     name: 'scheduled_timezone', label: ... }
 *
 * A searchable SelectInput rather than the plain <select> CurrencySelector
 * uses, because there are ~400 timezones to get through - and rather than a
 * DROPDOWN, whose ObjectsDropdown has no text input at all. AUTOSUGGEST
 * would let you type but stores the raw typed string without constraining
 * it to a real timezone.
 *
 * The form value is a whole TimezoneOption, not a bare string, since that's
 * what SelectInput hands back on change and expects back as its value.
 * Callers unwrap with `.value` (see getTimezoneOption).
 */
const TimezoneSelector: FC<Props> = ({
  value,
  onChange,
  onValidChange,
  name,
  required,
  disabled,
  isSubmitting,
  clearable = false,
  placeholder,
  className,
  autoFocus,
}) => {
  const { formatMessage } = useIntl();

  // Filters the full list locally - no network, so loadOptions' async escape
  // hatch is never needed. basicDropdownStringSearch would almost do, except
  // it passes every query straight through to the filter: SelectInput
  // re-invokes loadOptions with the *currently selected* option's own name as
  // the query whenever the menu reopens with nothing actually typed (to keep
  // the list "centered" on the current value), which would narrow ~400
  // timezones down to the one already chosen and make picking a different one
  // impossible without clearing the box first. So that query is treated as
  // "no filter" - the same fix CreateCycleModal's loadCampaignSources applies.
  const loadOptions = useCallback(
    (query: string | undefined, callback: (options: unknown[]) => void) => {
      const isImplicitReopenQuery = query != null && query === value?.name;
      callback(
        myOptionFilter(TIMEZONE_OPTIONS, isImplicitReopenQuery ? '' : query)
      );
    },
    [value?.name]
  );

  return (
    // No isOptionSelected override needed: every option's `value` is the
    // IANA name, so react-select's default matching is a string comparison.
    // CreateCycleModal only needs one because its options carry an object.
    <SelectInput
      name={name}
      value={value}
      onChange={onChange}
      onValidChange={onValidChange}
      loadOptions={loadOptions}
      required={required}
      disabled={disabled || isSubmitting}
      clearable={clearable}
      className={className}
      autoFocus={autoFocus}
      placeholder={
        placeholder ??
        formatMessage({
          id: 'app.views.widgets.inputs.timezone_selector.placeholder',
          defaultMessage: 'Search timezones',
        })
      }
    />
  );
};

export default React.memo(TimezoneSelector);
